import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Font } from '@feathr/blackbox';
import { Button, DebouncedInput, Input, TanStackTable } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import type { TCollectionTableSort } from '@feathr/extender/hooks';
import { useCollectionTable } from '@feathr/extender/hooks';
import { useLocalUrl, useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import fontsColumns from './fontsColumns';

const PAGE_SIZE = 10;
const INITIAL_SORT: TCollectionTableSort = [{ desc: false, id: 'name' }];

function FontsPage(): JSX.Element {
  const { Fonts } = useStore();
  const localUrl = useLocalUrl();
  const [search, setSearch] = useState<string | undefined>();
  const { t } = useTranslation();

  function handleDebouncedSearchChange(newValue?: string): void {
    setSearch(newValue);
  }

  const tableProps = useCollectionTable<Font>(Fonts, {
    defaultParams: { filters: { name__icontains: search } },
    initialPageSize: PAGE_SIZE,
    initialSort: INITIAL_SORT,
  });

  const columns = useMemo(() => fontsColumns(t), [t]);

  const filterElements = [
    null,
    <DebouncedInput<string>
      defaultValue={search}
      key={'search'}
      onChange={handleDebouncedSearchChange}
    >
      {(liveValue, onChangeLiveValue): JSX.Element => (
        <Input
          isClearable={true}
          onChange={onChangeLiveValue}
          placeholder={t('Search by name...')}
          prefix={getIconForAction('search')}
          type={'text'}
          value={liveValue}
        />
      )}
    </DebouncedInput>,
  ];

  return (
    <Page
      actions={[
        <Button key={'add'} link={localUrl('/settings/account/fonts/add')} type={'primary'}>
          {t('Add font')}
        </Button>,
      ]}
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={t('Fonts can be used in banner, email, and page templates.')}
      title={t('Fonts')}
    >
      <TanStackTable<Font>
        {...tableProps}
        columns={columns}
        filterElements={filterElements}
        noDataText={t('No fonts')}
      />
    </Page>
  );
}

export default observer(FontsPage);
